import { useEffect, useMemo, useState } from 'react';
import { Aviso } from '@/components/ui/aviso';
import { agruparPorDia, textoDelDia, textoDeVacias, tituloDelDia } from '../../lib/corridas';
import { diaCorto, diaDeBolivia, type RangoDeFechas } from '../../lib/rango-de-fechas';
import type { EventoDeLaLinea } from '../../types/corridas.types';
import { TarjetaDeEvento } from './tarjeta-de-evento';

/** Por día, de la más nueva a la más vieja (maqueta: `timelineHTML`). */
export function LineaDeTiempo({
  eventos,
  hoy,
  dentroDeLaEstrategia = false,
  nombreDeLaParte,
  resaltada,
  puedeCorrer,
  puedeReintentar,
  onVerProductos,
  onVolverAIntentar,
}: {
  eventos: EventoDeLaLinea[];
  hoy: string;
  dentroDeLaEstrategia?: boolean;
  nombreDeLaParte?: string | null;
  resaltada?: string | null;
  puedeCorrer?: boolean;
  puedeReintentar?: (estrategiaId: string) => boolean;
  onVerProductos?: (e: EventoDeLaLinea) => void;
  onVolverAIntentar?: (estrategiaId: string) => void;
}) {
  const dias = useMemo(() => agruparPorDia(eventos), [eventos]);
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  // Ronda final de R4 (QA E21): tocar en el gráfico una columna que quedó plegada
  // en «N corridas sin cambios» despliega su grupo (en el mismo render, así la
  // vista la encuentra para llevarla a la vista) y lo deja abierto.
  const grupoDeLaResaltada = useMemo(() => {
    if (!resaltada) return null;
    for (const d of dias)
      for (const item of d.items)
        if (item.tipo === 'vacias' && item.eventos.some((x) => x.id === resaltada))
          return item.clave;
    return null;
  }, [dias, resaltada]);
  useEffect(() => {
    if (grupoDeLaResaltada)
      setAbiertas((s) => (s.has(grupoDeLaResaltada) ? s : new Set(s).add(grupoDeLaResaltada)));
  }, [grupoDeLaResaltada]);
  const tarjeta = (e: EventoDeLaLinea) => (
    <TarjetaDeEvento
      key={e.id}
      evento={e}
      dentroDeLaEstrategia={dentroDeLaEstrategia}
      nombreDeLaParte={nombreDeLaParte}
      resaltada={resaltada === e.id}
      puedeCorrer={puedeCorrer}
      puedeReintentar={puedeReintentar}
      onVerProductos={onVerProductos}
      onVolverAIntentar={onVolverAIntentar}
    />
  );
  return (
    <div className="space-y-5" data-testid="linea-de-tiempo">
      {dias.map((d) => (
        <section key={d.dia} aria-label={tituloDelDia(d.dia, hoy)} className="space-y-2">
          <div className="flex items-baseline justify-between gap-3 border-b pb-1">
            <h3 className="min-w-0 text-sm font-semibold [overflow-wrap:anywhere]">
              {tituloDelDia(d.dia, hoy)}
            </h3>
            <span className="shrink-0 text-xs text-muted-foreground">{textoDelDia(d)}</span>
          </div>
          {d.items.map((item) =>
            item.tipo === 'evento' ? (
              tarjeta(item.evento)
            ) : abiertas.has(item.clave) || item.clave === grupoDeLaResaltada ? (
              item.eventos.map(tarjeta)
            ) : (
              <button
                key={item.clave}
                type="button"
                data-testid="corridas-sin-cambios"
                data-caja
                onClick={() => setAbiertas((s) => new Set(s).add(item.clave))}
                className="block w-full min-w-0 rounded-tarjeta border border-dashed bg-card px-4 py-2.5 text-left text-sm text-muted-foreground [overflow-wrap:anywhere] hover:bg-muted/40"
              >
                {textoDeVacias(item, !dentroDeLaEstrategia)} ·{' '}
                <span className="text-primary">Ver</span>
              </button>
            )
          )}
        </section>
      ))}
    </div>
  );
}

/**
 * D-R2-6: si el período empieza antes del primer día con corridas guardadas (o
 * no hay ninguna), el aviso de la maqueta. Va al final de la lista.
 */
export function AvisoAntesDelRegistro({
  registroDesde,
  periodo,
}: {
  registroDesde: string | null;
  periodo: RangoDeFechas;
}) {
  if (registroDesde == null)
    return (
      <Aviso tono="info" titulo="Todavía no se guardó ninguna corrida.">
        Las corridas se guardan desde esta versión de Prixia: la próxima que corra va a aparecer
        acá.
      </Aviso>
    );
  const primerDia = diaDeBolivia(new Date(registroDesde));
  if (periodo.desde != null && periodo.desde >= primerDia) return null;
  return (
    <Aviso tono="info" titulo={`Antes del ${diaCorto(primerDia)} no se guardaban las corridas.`}>
      Hasta esa fecha Prixia sólo recordaba la última corrida de cada estrategia, así que para esos
      días no hay detalle por corrida. Las decisiones que tomaste siguen en «Por decidir», en
      Aprobadas y Descartadas.
    </Aviso>
  );
}
